export interface ProjectTemplate {
  id: string
  name: string
  project_type: string
  description: string
  icon: string
  default_duration_days: number
  milestones: {
    name: string
    sequence: number
    description: string
    duration_days: number
  }[]
  required_documents: {
    name: string
    description: string
    is_required: boolean
  }[]
}

export const projectTemplates: ProjectTemplate[] = [
  {
    id: 'life-insurance-application',
    name: 'Life Insurance Application',
    project_type: 'insurance_policy',
    description: 'Complete process for applying for a new life insurance policy',
    icon: '🛡️',
    default_duration_days: 45,
    milestones: [
      {
        name: 'Needs Analysis',
        sequence: 1,
        description: 'Complete comprehensive needs analysis to determine coverage requirements',
        duration_days: 3
      },
      {
        name: 'Quote Preparation',
        sequence: 2,
        description: 'Prepare and present insurance quotes from multiple carriers',
        duration_days: 5
      },
      {
        name: 'Application Submission',
        sequence: 3,
        description: 'Complete and submit application with chosen carrier',
        duration_days: 2
      },
      {
        name: 'Underwriting',
        sequence: 4,
        description: 'Medical exams and underwriting process',
        duration_days: 21
      },
      {
        name: 'Policy Delivery',
        sequence: 5,
        description: 'Deliver policy and explain coverage details',
        duration_days: 3
      }
    ],
    required_documents: [
      {
        name: 'Needs Analysis Form',
        description: 'Completed financial needs analysis',
        is_required: true
      },
      {
        name: 'Application Form',
        description: 'Signed insurance application',
        is_required: true
      },
      {
        name: 'Medical History',
        description: 'Medical questionnaire and history',
        is_required: true
      }
    ]
  },
  {
    id: 'investment-transfer',
    name: 'Investment Account Transfer',
    project_type: 'investment_transfer',
    description: 'Transfer existing investments to new managed accounts',
    icon: '💼',
    default_duration_days: 30,
    milestones: [
      {
        name: 'Account Setup',
        sequence: 1,
        description: 'Set up new investment accounts and complete KYC',
        duration_days: 3
      },
      {
        name: 'Transfer Forms',
        sequence: 2,
        description: 'Complete transfer authorization forms for existing accounts',
        duration_days: 2
      },
      {
        name: 'Fund Selection',
        sequence: 3,
        description: 'Select appropriate investment funds based on risk profile',
        duration_days: 3
      },
      {
        name: 'Processing',
        sequence: 4,
        description: 'Monitor transfer processing and follow up as needed',
        duration_days: 14
      },
      {
        name: 'Confirmation',
        sequence: 5,
        description: 'Confirm transfer completion and review holdings',
        duration_days: 2
      }
    ],
    required_documents: [
      {
        name: 'Account Statements',
        description: 'Recent statements from transferring accounts',
        is_required: true
      },
      {
        name: 'Transfer Forms',
        description: 'Completed transfer authorization forms',
        is_required: true
      },
      {
        name: 'Investment Policy Statement',
        description: 'Signed IPS outlining investment objectives',
        is_required: true
      }
    ]
  },
  {
    id: 'estate-planning-review',
    name: 'Estate Planning Review',
    project_type: 'estate_planning',
    description: 'Comprehensive review and update of estate planning documents',
    icon: '📋',
    default_duration_days: 60,
    milestones: [
      {
        name: 'Document Gathering',
        sequence: 1,
        description: 'Collect all existing estate planning documents',
        duration_days: 7
      },
      {
        name: 'Analysis',
        sequence: 2,
        description: 'Analyze current estate plan and identify gaps or updates needed',
        duration_days: 10
      },
      {
        name: 'Recommendations',
        sequence: 3,
        description: 'Prepare detailed estate planning recommendations',
        duration_days: 7
      },
      {
        name: 'Implementation',
        sequence: 4,
        description: 'Work with lawyers to implement recommended changes',
        duration_days: 30
      },
      {
        name: 'Filing',
        sequence: 5,
        description: 'Ensure all documents are properly filed and stored',
        duration_days: 3
      }
    ],
    required_documents: [
      {
        name: 'Current Will',
        description: 'Copy of existing will',
        is_required: false
      },
      {
        name: 'Power of Attorney',
        description: 'Current POA documents',
        is_required: false
      },
      {
        name: 'Asset Summary',
        description: 'Complete list of assets and liabilities',
        is_required: true
      }
    ]
  },
  {
    id: 'retirement-planning',
    name: 'Retirement Planning Analysis',
    project_type: 'retirement_planning',
    description: 'Comprehensive retirement planning and projection analysis',
    icon: '🎯',
    default_duration_days: 21,
    milestones: [
      {
        name: 'Data Collection',
        sequence: 1,
        description: 'Gather all financial information and retirement goals',
        duration_days: 5
      },
      {
        name: 'Current State Analysis',
        sequence: 2,
        description: 'Analyze current financial position and retirement readiness',
        duration_days: 3
      },
      {
        name: 'Projection Modeling',
        sequence: 3,
        description: 'Create retirement projections and scenarios',
        duration_days: 5
      },
      {
        name: 'Strategy Development',
        sequence: 4,
        description: 'Develop strategies to achieve retirement goals',
        duration_days: 5
      },
      {
        name: 'Implementation Plan',
        sequence: 5,
        description: 'Create actionable implementation plan with timelines',
        duration_days: 3
      }
    ],
    required_documents: [
      {
        name: 'Income Statements',
        description: 'Recent pay stubs or income verification',
        is_required: true
      },
      {
        name: 'Retirement Account Statements',
        description: 'Current balances for all retirement accounts',
        is_required: true
      },
      {
        name: 'Expense Summary',
        description: 'Detailed monthly expense breakdown',
        is_required: true
      }
    ]
  },
  {
    id: 'tax-planning-review',
    name: 'Tax Planning Review',
    project_type: 'tax_planning',
    description: 'Annual tax planning review and optimization',
    icon: '📊',
    default_duration_days: 14,
    milestones: [
      {
        name: 'Tax Document Collection',
        sequence: 1,
        description: 'Gather previous year tax returns and current year estimates',
        duration_days: 3
      },
      {
        name: 'Tax Situation Analysis',
        sequence: 2,
        description: 'Analyze current tax situation and identify opportunities',
        duration_days: 3
      },
      {
        name: 'Strategy Development',
        sequence: 3,
        description: 'Develop tax optimization strategies',
        duration_days: 3
      },
      {
        name: 'Implementation',
        sequence: 4,
        description: 'Implement approved tax strategies',
        duration_days: 5
      }
    ],
    required_documents: [
      {
        name: 'Tax Returns',
        description: 'Previous 2 years tax returns',
        is_required: true
      },
      {
        name: 'Current Year Income',
        description: 'Year-to-date income statements',
        is_required: true
      }
    ]
  },
  {
    id: 'education-savings-setup',
    name: 'Education Savings Plan Setup',
    project_type: 'education_planning',
    description: 'Set up education savings accounts for children',
    icon: '🎓',
    default_duration_days: 14,
    milestones: [
      {
        name: 'Needs Assessment',
        sequence: 1,
        description: 'Determine education funding needs and timeline',
        duration_days: 2
      },
      {
        name: 'Account Selection',
        sequence: 2,
        description: 'Choose appropriate education savings vehicle (RESP, 529, etc)',
        duration_days: 2
      },
      {
        name: 'Account Opening',
        sequence: 3,
        description: 'Complete account opening and beneficiary designation',
        duration_days: 3
      },
      {
        name: 'Investment Selection',
        sequence: 4,
        description: 'Select age-appropriate investment options',
        duration_days: 2
      },
      {
        name: 'Funding Setup',
        sequence: 5,
        description: 'Set up initial funding and automatic contributions',
        duration_days: 2
      }
    ],
    required_documents: [
      {
        name: 'Birth Certificate',
        description: 'Child\'s birth certificate for beneficiary setup',
        is_required: true
      },
      {
        name: 'SIN/SSN',
        description: 'Social insurance/security numbers',
        is_required: true
      }
    ]
  }
]

export function getTemplateById(id: string): ProjectTemplate | undefined {
  return projectTemplates.find(template => template.id === id)
}

export function getTemplatesByType(type: string): ProjectTemplate[] {
  return projectTemplates.filter(template => template.project_type === type)
}

export function calculateMilestoneDates(
  template: ProjectTemplate, 
  startDate: Date = new Date()
): Array<{ name: string; dueDate: Date }> {
  let currentDate = new Date(startDate)
  
  return template.milestones.map(milestone => {
    const dueDate = new Date(currentDate)
    dueDate.setDate(dueDate.getDate() + milestone.duration_days)
    currentDate = new Date(dueDate)
    
    return {
      name: milestone.name,
      dueDate
    }
  })
}